import { Player } from './player';
import { AudioPlayerMode, IAudioPlayer, ILogger, PlayOption } from '../../enums';
import { NAME } from '../constants';
import { VolumeMeterConnector } from '../volume-meter/volume-meter';
import { IOS_VERSION, IS_ANY_SAFARI, IS_FIREFOX, isRealIOS, IS_WECHAT, IS_XWEB, isVersionLargerThan } from '../../utils/environment';
import { getAudioContext, startFakeRunningDetect } from '../../utils/audio-context';
import { AnalyserNodeConnector, AudioNodeConnector, AudioPipeline } from '../../manager/audio-mixer';
import Logger from '../../utils/log/logger';
import { PlayerEvent } from '../event';
import * as eventManager from '../../manager/event-manager';
import { logSpeakers } from '../device-detector';
import { isNumber } from '../../utils/utils.js';
export interface AudioAttr {
  id: string,
  style: any;
}
// 解决 Safari 音频自动播放弹窗的问题
// https://tapd.woa.com/20396022/bugtrace/bugs/view/1020396022125341085
let cachedElement: HTMLAudioElement | undefined;

export interface AudioPlayerOptions {
  id: string;
  url?: string;
  track: MediaStreamAudioTrack | null;
  container: HTMLElement | null;
  muted: boolean;
  log: Logger;
  enableTimeupdateEvent?: boolean;
  enableVolumeControlInIOS?: boolean;
  enableLogTrackState?: boolean;
}
class PlayerPipeline extends AudioPipeline {
  volumeMeter: VolumeMeterConnector;
  volumeMeterAfter3A: VolumeMeterConnector;
  volumeDestination: AudioNodeConnector<AudioDestinationNode>;
  analyser = new AnalyserNodeConnector();
  constructor(public log: ILogger) {
    super();
    this.volumeMeter = new VolumeMeterConnector({ log: this.log });
    this.volumeMeterAfter3A = new VolumeMeterConnector({ log: this.log });
    this.volumeDestination = new AudioNodeConnector();
    this.volumeMeter.pipeTo(this.volumeDestination);
  }
  destroy() {
    this.gain.deleteNode();
    this.volumeMeter.deleteNode();
    this.analyser.deleteNode();
    this.source.deleteNode();
    this.destination.deleteNode();
    this.volumeDestination.deleteNode();
  }
}
export class AudioPlayer extends Player<typeof NAME.AUDIO> implements IAudioPlayer {
  private _outputDeviceId: string;
  protected _floatVolume = 1;
  protected _destination?: MediaStreamAudioDestinationNode;
  pipeline: PlayerPipeline;
  volumeMeterMode: 'worklet' | 'analyser' | 'none' = 'worklet';
  enableVolumeControlInIOS?: boolean;
  constructor(options: AudioPlayerOptions) {
    super(options, NAME.AUDIO);
    this.enableVolumeControlInIOS = options.enableVolumeControlInIOS;
    this.mode = AudioPlayerMode.ELEMENT;
    if (options.url) this.url = options.url;
    this.pipeline = new PlayerPipeline(this._log);
  }

  setTrack(track: MediaStreamAudioTrack | null) {

  }
  get duration() {
    return Math.floor((this.element?.duration || 0) * 1000);
  }

  get currentTime() {
    return Math.floor((this.element?.currentTime || 0) * 1000);
  }

  set currentTime(time: number) {
    if (this.element) {
      this.element.currentTime = time / 1000;
    }
  }

  getMediaStream() {
    return this.pipeline.stream || (this.track ? new MediaStream([this.track]) : null);
  }

  initializeElement(volume?: number) {
    const isBugDevice = IOS_VERSION === '15.2' || IOS_VERSION === '15.3' || IOS_VERSION === '15.4';
    if (isBugDevice && this.muted) {
      this._log.info('audioElement is muted.');
      return;
    }
    const element = cachedElement || new Audio();
    element.setAttribute('autoplay', 'autoplay');
    element.srcObject = this.getMediaStream();
    element.muted = this.muted;
    if (this.url) {
      element.crossOrigin = 'anonymous';
      element.src = this.url;
    }
    this.element = element;
    this.setVolume(isNumber(volume) ? volume / 100 : this._floatVolume);
    if (element === cachedElement) cachedElement = void 0;
    if (this.options.enableTimeupdateEvent) {
      this.element.ontimeupdate = () => this.emit(PlayerEvent.TIME_UPDATE, this.currentTime);
    }
    this.bindElementEvents();
    this._log.info('initializeElement');
  }
  async play(options?: PlayOption) {
    if (!this.track && !this.url) return;
    try {
      if (!this.pipeline.source.node && this.track) this.pipeline.replaceSource(this.track);
      if (!this.element) this.initializeElement(options?.volume);
      if (this._outputDeviceId) await this.setSinkId(this._outputDeviceId);
      if (this.volumeMeterMode === 'worklet') {
        this.pipeline.volumeMeter.init();
        this.pipeline.volumeMeterAfter3A.init();
      } else if (this.volumeMeterMode === 'analyser') this.pipeline.analyser.setNode(getAudioContext('player').createAnalyser());
      // 第一次播放时，打印扬声器列表
      logSpeakers();
    } catch (error) {
      this._log.warn(`audio play error: ${error}`);
      if (isVersionLargerThan(IOS_VERSION, '18.7', true)) {
        // iOS 18.7 微信 没有走到super play就触发了自动播放异常，提前绑定事件避免恢复失败
        this.bindAutoPlayEvent();
      }
      throw error;
    }
    return super.play();
  }
  stop(jitterBufferDelay = 0) {
    this.pipeline.destroy();
    super.stop(jitterBufferDelay);
  }
  setVolume(volume: number) {
    this._floatVolume = volume;
    if (this.element) this.element.volume = volume;
  }
  async setSinkId(deviceId: string) {
    if (this._outputDeviceId !== deviceId) {
      this._outputDeviceId = deviceId;
    }
    // @ts-ignore
    if (this.element && this.element.sinkId !== deviceId) {
      // @ts-ignore
      await this.element.setSinkId?.(deviceId);
    }
  }
  get useDestination() {
    return !!this.pipeline.stream;
  }
  setLoop(loop: boolean) {
    if (!this.element) {
      return;
    }
    this.element.loop = loop;
  }

  /**
   * 用于获取 0.0X 丢失精度的音量数据
   */
  getAudioLevel() {
    return this.pipeline.volumeMeter.getCalculatedVolume();
  }

  /**
   * 用于获取 0.00...X 未丢失精度的音量数据
   */
  getInternalAudioLevel() {
    return this.pipeline.volumeMeter.getInternalAudioLevel();
  }

  /**
   * 用于获取 3A 处理后的 0.00...X 未丢失精度的音量数据
   */
  getInternalAudioLevelAfter3A() {
    return this.pipeline.volumeMeterAfter3A.getInternalAudioLevel();
  }
}
export class LocalAudioPlayer extends AudioPlayer {
  setTrack(track: MediaStreamAudioTrack | null): void {
    if (this.track !== track) {
      this.unbindTrackEvents();
      this.track = track;
      this.emit(PlayerEvent.MEDIA_TRACK_CHANGED, track);
      if (track) {
        this.bindTrackEvents();
        if (this.element) this.element.srcObject = new MediaStream([track]);
      }
    }
  }
}
export class RemoteAudioPlayer extends AudioPlayer {
  private _sourceElement?: HTMLAudioElement;
  private _output = new AudioNodeConnector<AudioDestinationNode>();
  constructor(options: AudioPlayerOptions) {
    super(options);
    this.pipeline.source.pipeTo(this.pipeline.gain);
    this.pipeline.gain.pipeTo(this.pipeline.volumeMeter).pipeTo(this._output);
    this.pipeline.gain.pipeTo(this.pipeline.destination);
  }
  setOutput() {
    this.mode = AudioPlayerMode.CONTEXT;
    this._output.setNode(getAudioContext('player').destination);
  }
  write(audioData: AudioData) {
    this.pipeline.volumeMeter.write(audioData);
  }
  setTrack(track: MediaStreamAudioTrack | null): void {
    if (this.element?.error?.code === MediaError.MEDIA_ERR_DECODE) {
      return;
    }
    if (this.track !== track) {
      this.unbindTrackEvents();
      this.track = track;
      this.emit(PlayerEvent.MEDIA_TRACK_CHANGED, track);
      if (track) {
        this.bindTrackEvents();
        if (this._sourceElement) this._sourceElement.srcObject = new MediaStream([track]);
        else if (!this.useDestination && this.element) {
          // 有增益的时候 element 的 srcObject 是 pipeline 的 destination
          this.element.srcObject = new MediaStream([track]);
        }
        this.pipeline.source.channelCount = track.getSettings()?.channelCount || 1;
        this.pipeline.replaceSource(track);
      } else this.pipeline.source.deleteNode();
    }
  }
  setVolume(volume: number) {
    const IS_SET_ELEMENT_VOLUME = volume <= 1 && !isRealIOS();

    if (this._floatVolume === volume) {
      if (IS_SET_ELEMENT_VOLUME && this.element?.volume === volume) return;
      if (!IS_SET_ELEMENT_VOLUME && this.pipeline.volume === volume) return;
    }
    this._floatVolume = volume;

    if (this.useDestination) {
      this.pipeline.setVolume(volume);
      this._log.info(`set pipeline volume: ${volume}`);
    } else if (IS_SET_ELEMENT_VOLUME) {
      // iOS 不支持 js 设置 element.volume
      // docs: https://developer.apple.com/library/archive/documentation/AudioVideo/Conceptual/Using_HTML5_Audio_Video/Device-SpecificConsiderations/Device-SpecificConsiderations.html
      if (this.element) {
        this._log.info(`set element volume: ${volume}`);
        this.element.volume = volume;
      } else this._log.info('set element volume: no element');
    } else {
      // iOS 使用 audioContext 时, 切换别的 app 播放声音, 会导致网页无声
      // webkit bug: https://bugs.webkit.org/show_bug.cgi?id=283419
      if (isRealIOS()) {
        if (this.enableVolumeControlInIOS) startFakeRunningDetect();
        else return;
      }
      if (IS_FIREFOX && !this.pipeline.source.node) {
        this._log.warn('set pipeline volume failed: no source node');
        return;
      }
      this._log.info(`start set pipeline volume: ${volume}`);
      this.pipeline.setVolume(volume);
      if (this.element && !this._sourceElement) {
        if (!this._destination) this._destination = getAudioContext('player').createMediaStreamDestination();
        this.pipeline.destination.setNode(this._destination);
        eventManager.remove(this.element);
        // 原始 track 必须放在 audio 标签中播放，然后才能使用 audio context。
        // chrome bug: https://bugs.chromium.org/p/chromium/issues/detail?id=1479349
        this._sourceElement = this.element;
        this._sourceElement.muted = true;
        this.element = null;
        this.play().catch(error => {
          this.emit(PlayerEvent.AUTOPLAY_FAILED, error);
        });
      }
    }
  }
  stop(jitterBufferDelay = 0) {
    this.pipeline.destroy();
    const element = this._sourceElement || this.element;
    if (element && IS_ANY_SAFARI) cachedElement = element;
    if (this._sourceElement) {
      this._sourceElement.srcObject = null;
      delete this._sourceElement;
    }
    super.stop(jitterBufferDelay);
  }

  // createNewDestination() {
  //   if (this._destination) this.pipeline.destination.deleteNode();
  //   this._destination = getAudioContext('player').createMediaStreamDestination();
  //   this.pipeline.destination.setNode(this._destination);
  //   if (this.element) this.element.srcObject = this.getMediaStream();
  //   return this.play();
  // }

  // iOS 26.1 可能 destination 会无声，这个方法用于检查 destination 是否失活
  // async checkDestinationActivity(context = getAudioContext(), destinationNode = this._destination) {
  //   // 1. 防御性检查
  //   if (context.state !== 'running') return false;

  //   // 2. 从 Destination 的流创建一个临时的 Source
  //   // 这是关键：我们不检查节点本身，而是检查它生成的流
  //   const { stream } = destinationNode;
  //   if (!stream.active) return false;
  //   // 3. 建立分析链路
  //   const probeSource = context.createMediaStreamSource(stream);
  //   const analyser = context.createAnalyser();
  //   // 设置一个小尺寸的 FFT 以提高性能，只需检测有无能量即可
  //   analyser.fftSize = 256;
  //   probeSource.connect(analyser);
  //   // 4. 读取数据
  //   // 我们需要延时一小会儿，确保数据流过管道
  //   await new Promise(resolve => setTimeout(resolve, 50));
  //   const dataArray = new Uint8Array(analyser.frequencyBinCount);
  //   analyser.getByteFrequencyData(dataArray);
  //   // 5. 计算能量
  //   // 只要数组里有非 0 值，就说明有声音
  //   const hasSignal = dataArray.some(value => value > 0);
  //   // 6. 清理资源 (非常重要，否则会造成内存泄漏和 iOS 上的连接限制)
  //   probeSource.disconnect();
  //   // 注意：不要关闭 stream，因为它是业务在用的流
  //   return hasSignal;
  // }
}
